/**
 * How much of a session's volume opened positions, and how much closed them.
 *
 * The split is NOT determinable, and the formula this replaces — opening = (vol + ΔOI)/2,
 * closing = (vol − ΔOI)/2 — quietly assumed it was. Write the session as
 *
 *     V = A + B + M        A = volume where BOTH sides opened
 *     ΔOI = A − B          B = volume where BOTH sides closed
 *                          M = volume where one side opened against the other closing
 *
 * M never touches open interest, so it is invisible in ΔOI and can be anything from zero
 * to the whole session. Solving with M free:
 *
 *     A ∈ [ max(ΔOI, 0),  (V + ΔOI)/2 ]
 *     B ∈ [ max(−ΔOI, 0), (V − ΔOI)/2 ]
 *
 * The old formula is the upper end of each — the case where no trade was ever a transfer
 * between an opener and a closer. That is the least likely case in a liquid contract, and
 * it was being drawn as though it were the measurement.
 *
 * The lower end is the part that IS forced: open interest cannot rise by ΔOI unless at
 * least ΔOI contracts were created. That number is worth showing precisely because nothing
 * can argue with it.
 *
 * ATTRIBUTION. The `open_interest` on an EOD row reflects trading through the session
 * BEFORE it -- OPRA publishes the figure the following morning. Verified on SPXW 7785C:
 * 461 traded on 08-18 and open interest went 61 -> 434 on the 08-19 row, and every other
 * jump in the series lands on the row after the volume that caused it. So a session is
 * confirmed by the NEXT row's open interest, never its own, exactly as eodPositionEffect
 * does it. Differencing against the previous row instead pairs a session's volume with the
 * prior session's flow, which is off by one day and produces impossible readings.
 */

export interface OpenCloseBoundsInput {
  date: string;
  volume?: number | null;
  open_interest?: number | null;
  /** The in-progress session: its open interest has not been published, so ΔOI is unknown. */
  is_today?: boolean;
}

export interface SessionOpenCloseBounds {
  date: string;
  volume: number;
  /** Published change in open interest, or null when it cannot be known yet. */
  deltaOi: number | null;
  /** Share of the session's volume that MUST have opened, as a percentage. */
  openMinPct: number | null;
  /** The most of it that COULD have opened. */
  openMaxPct: number | null;
  closeMinPct: number | null;
  closeMaxPct: number | null;
  /** Width of the opening band — what the data does not pin down. */
  openRangePct: number | null;
  closeRangePct: number | null;
  /** |ΔOI| exceeds volume: the two figures cannot both be right, so neither is used. */
  inconsistent: boolean;
  /** No ΔOI available (first session in range, or still in progress). */
  unresolved: boolean;
}

function n(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function openCloseBounds(rows: readonly OpenCloseBoundsInput[]): SessionOpenCloseBounds[] {
  const sorted = [...rows].sort((a, b) => String(a.date).localeCompare(String(b.date)));

  return sorted.map((row, i) => {
    const volume = n(row.volume);
    const oi = n(row.open_interest);
    // A session is confirmed by the NEXT row's published open interest. The in-progress row
    // is excluded on purpose: its open interest is EITHER this morning's publication (which
    // would settle the previous session) OR the prior row's figure carried forward when no
    // publication was available — and the row carries nothing to tell those apart. Treating a
    // carried figure as a confirmation would report an unchanged open interest as "flat" when
    // the truth is that nothing has been published. The caller that DOES know the provenance
    // passes it explicitly; see `publishedOi` in eodPositionEffect.
    const next = sorted[i + 1];
    const confirmed = next && !next.is_today ? next : undefined;

    const blank = {
      date: row.date,
      volume,
      deltaOi: null,
      openMinPct: null,
      openMaxPct: null,
      closeMinPct: null,
      closeMaxPct: null,
      openRangePct: null,
      closeRangePct: null,
    };

    // Still in progress, not yet confirmed by a following session, or nothing traded.
    if (row.is_today || confirmed === undefined || volume <= 0) {
      return { ...blank, inconsistent: false, unresolved: true };
    }

    const deltaOi = n(confirmed.open_interest) - oi;
    // Open interest cannot move further than the volume that moved it. When it does, the
    // two came from different sources or the contract was adjusted; asserting a split on
    // top of that would be dressing up bad data.
    if (Math.abs(deltaOi) > volume) {
      return { ...blank, deltaOi, inconsistent: true, unresolved: true };
    }

    const openMin = Math.max(deltaOi, 0);
    const openMax = (volume + deltaOi) / 2;
    const closeMin = Math.max(-deltaOi, 0);
    const closeMax = (volume - deltaOi) / 2;
    const pct = (v: number) => (100 * v) / volume;

    return {
      date: row.date,
      volume,
      deltaOi,
      openMinPct: pct(openMin),
      openMaxPct: pct(openMax),
      closeMinPct: pct(closeMin),
      closeMaxPct: pct(closeMax),
      openRangePct: pct(openMax - openMin),
      closeRangePct: pct(closeMax - closeMin),
      inconsistent: false,
      unresolved: false,
    };
  });
}
